import type { SxProps, Theme } from '@mui/material';

export const dialogPaper: SxProps<Theme> = {
  borderRadius: 2,
};

export const dialogTitle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  pb: 2,
};

export const subtitle: SxProps<Theme> = {
  mt: 0.5,
  fontWeight: 400,
};

export const closeButton: SxProps<Theme> = {
  color: (theme) => theme.palette.grey[500],
};

export const loadingContainer: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  py: 4,
};

export const errorContainer: SxProps<Theme> = {
  py: 2,
};

export const retryButton: SxProps<Theme> = {
  mt: 2,
};

export const noTracksText: SxProps<Theme> = {
  py: 2,
};

export const tracksContainer: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

export const trackCard: SxProps<Theme> = {
  p: 2.5,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  backgroundColor: 'background.paper',
};

export const trackCardContent = (mb: number): SxProps<Theme> => ({
  display: 'flex',
  gap: 2,
  mb,
});

export const rankBadge: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  width: 16,
  pt: 0.75,
  color: 'text.disabled',
  fontSize: '0.65rem',
  fontWeight: 500,
  flexShrink: 0,
};

export const artworkContainer: SxProps<Theme> = {
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  width: 70,
  height: 70,
  borderRadius: 1.5,
  backgroundColor: 'grey.100',
  overflow: 'hidden',
  flexShrink: 0,
};

export const artworkImage: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

export const artworkPlaceholder: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  background:
    'linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(63, 81, 181, 0.1) 100%)',
};

export const artworkPlaceholderIcon: SxProps<Theme> = {
  fontSize: 28,
  color: 'text.secondary',
  opacity: 0.4,
};

export const playButton = (isPlaying: boolean): SxProps<Theme> => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: isPlaying ? 'primary.main' : 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  width: 36,
  height: 36,
  opacity: isPlaying ? 1 : 0,
  transition: 'opacity 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: isPlaying ? 'primary.dark' : 'rgba(0, 0, 0, 0.8)',
    opacity: 1,
  },
});

export const playPauseIcon: SxProps<Theme> = {
  fontSize: 20,
};

export const trackInfoContainer: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
};

export const trackInfoInner: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const trackTitle: SxProps<Theme> = {
  mb: 0.25,
};

export const trackArtist: SxProps<Theme> = {
  mb: 0.5,
};

export const similarityText = (similarityPercent: number): SxProps<Theme> => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  color:
    similarityPercent >= 85
      ? 'success.main'
      : similarityPercent >= 70
        ? 'primary.main'
        : 'text.secondary',
});

export const similarityPercent: SxProps<Theme> = {
  opacity: 0.5,
};

export const thumbsContainer: SxProps<Theme> = {
  position: 'relative',
  width: 90,
  height: 70,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pb: 0.5,
};

export const diagonalSeparator: SxProps<Theme> = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: '2px',
  height: '110%',
  backgroundColor: 'divider',
  opacity: 0.9,
  transform: 'translate(-50%, -50%) rotate(45deg)',
  transformOrigin: 'center',
  zIndex: 0,
};

export const thumbsUpButton: SxProps<Theme> = {
  position: 'absolute',
  top: -2,
  left: -2,
  color: 'success.main',
  backgroundColor: 'success.50',
  border: '1.5px solid',
  borderColor: 'success.main',
  width: 44,
  height: 44,
  zIndex: 1,
  '&:hover': {
    backgroundColor: 'success.main',
    color: 'white',
    transform: 'scale(1.1)',
  },
  transition: 'all 0.2s ease-in-out',
};

export const thumbsDownButton: SxProps<Theme> = {
  position: 'absolute',
  bottom: -2,
  right: -2,
  color: 'error.main',
  backgroundColor: 'error.50',
  border: '1.5px solid',
  borderColor: 'error.main',
  width: 44,
  height: 44,
  zIndex: 1,
  '&:hover': {
    backgroundColor: 'error.main',
    color: 'white',
    transform: 'scale(1.1)',
  },
  transition: 'all 0.2s ease-in-out',
};

export const thumbsIcon: SxProps<Theme> = {
  fontSize: 22,
};

export const vibeTagsContainer: SxProps<Theme> = {
  mt: 1.5,
  pt: 1.5,
  borderTop: '1px solid',
  borderColor: 'divider',
};

export const vibeTagsLabel: SxProps<Theme> = {
  mb: 0.75,
  display: 'block',
  fontSize: '0.7rem',
};

export const vibeTagsList: SxProps<Theme> = {
  display: 'flex',
  gap: 0.5,
  flexWrap: 'wrap',
};

export const vibeTagChip: SxProps<Theme> = {
  fontSize: '0.7rem',
  height: 22,
  opacity: 0.7,
  borderColor: 'grey.300',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  '&:hover': {
    opacity: 1,
    borderColor: 'primary.main',
    backgroundColor: 'primary.50',
  },
  transition: 'all 0.2s ease-in-out',
};

export const dialogContentText: SxProps<Theme> = {
  mb: 2,
};

export const feedbackButtonsContainer: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

export const feedbackButton: SxProps<Theme> = {
  py: 1.5,
  textAlign: 'left',
  justifyContent: 'flex-start',
};

export const notSoundGrid: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 2,
};

export const otherInputContainer: SxProps<Theme> = {
  mt: 2,
};

export const whichSongButton: SxProps<Theme> = {
  py: 2,
  textAlign: 'left',
  justifyContent: 'flex-start',
};

export const shortcutHint: SxProps<Theme> = {
  display: 'block',
  mt: 0.5,
};
